<script lang="ts" setup>
import { RouterView, useRoute } from 'vue-router';
import { MenuComp, HeaderNormal, HeaderSmall, PinnedViewsContainer, AlertMessage } from '@/components';
import ToastContainer from '../toast/ToastContainer.vue';
import { onMounted, ref, watch } from 'vue';
import { useHelper, useKeyboardListener } from '@/composeables';
import { NotAuthorizedView } from '@/components/views';
import { useEventCommonStoreRef, useThemeStore } from '@/stores';
import { useDeviceStatus } from '@/composeables/devicestatus/devicestatus';
import { useAccessValidator } from '@/composeables/accessValidator';
import MessageContainer from '../MessageContainer.vue';
import { Router } from '@/objects/Router';
import DialogSystemModalStack from '../Modal/DialogSystemModalStack.vue';

// We just start the store here to make sure it is loaded globally
useDeviceStatus();

const route = useRoute();
const isFullscreen = ref<boolean>(false);

const { Event } = useEventCommonStoreRef();

// Register the global keyboard shortcuts within the app. This is done here to make sure it is only done once.
useKeyboardListener().registerGlobalShortcutKeys();

/**
 * Get a unique key for the view. This is used to force a rerender of the view when the view is changed.
 * At the moment this is ok for our use since we use the route to control all views.
 * This should also work with signalR and all other functionality in the view since it is still active in the background. but this need to be properly tested
 */
const getUniqueViewKey = () => `${String(Router.CurrentMatchedRoute?.name)}-${Router.RouteParams?.eventID}`;

/**
 * Called when the view is changed for a new view.
 */
const viewChanged = () => {
    // Set focus to the first input element in the view
    useHelper().setFocus();
};

/**
 * Update the fullscreen value
 */
const updateFullscreen = () => {
    // Check if we have a fullscreen parameter in the route
    if (route == null) return;

    if (route.matched.length == 1 && ((route.matched[0].props['default'] as any)?.fullscreen as boolean)) {
        isFullscreen.value = true;
        return;
    }

    if (route.query == null) return;

    isFullscreen.value = route.query['fullscreen'] === 'true';
};

/**
 * We update the theme if it is passed from the query
 * This is only for legacy support to include the views in old admin and force the light theme
 */
const updateTheme = () => {
    if (route == null || route.query == null) {
        return;
    }

    let themeFromQuery = route.query['theme'] ?? '';

    if (['dark', 'light'].includes(themeFromQuery?.toString())) {
        const themeStore = useThemeStore();

        themeStore.setTheme(themeFromQuery as 'dark' | 'light');
    }
};

/**
 * We listen to changes in the route and update if needed.
 */
watch(
    () => route.query,
    (toParams, previousParams) => {
        if (toParams['fullscreen'] !== previousParams['fullscreen']) {
            updateFullscreen();
        }
        if (toParams['theme'] !== previousParams['theme']) {
            updateTheme();
        }
        viewChanged();
    },
);

onMounted(() => {
    updateFullscreen();
    updateTheme();
    viewChanged();
});
</script>
<!--
    We support 2 sizes, depending on screensize a pad could show desktop version
    - desktop
    - mobile

    Outer container is whole screen and no scrollbars. The scrolling will be done in the components
        .container-fluid 100% with  https://getbootstrap.com/docs/5.2/layout/containers/#how-they-work
        .vh-100 100% height  
        .overflow-hidden no scroll  

    Inside we have a flex layout that will layout as a column - so items above each other

    For big devices we show the head in this column so it is always visible, for small the header is placed in the content so we can scroll it away.

    Normal header
        .d-none shows as none
        .d-md-block shows for all over md
        .flex-shrink-1 use only space it needs

    Small header 
        .d-flex shows as flex
        .d-md-none hides for all over md
        .flex-shrink-1 use only space it needs


    Below we have the mainview containing menu and the content. This will use the rest of the screen space. For small devices we render the menu in head so we have a parameter for what we are building
        .d-flex for layout inside
        .flex-grow-1 to use rest of the space
        .mainview to make sure we dont use more than the rest of the space - se comment below

    Inide this again we have the menu, it will "know" where it is placed so it knows there is flex layout etc. This contains the header for small devices
    The rest of the space is for the routerview and will scroll
        .flex-grow-1 use rest of the space horizontal
        .overflow-auto scroll if whatever inside is bigger
        .h-100 fill parent container vertical
-->
<template>
    <div
        class="container-fluid overflow-hidden vh-100 p-0"
        :class="{ 'eq-fullscreen': isFullscreen }"
    >
        <div class="d-flex flex-column vh-100">
            <HeaderNormal v-if="!isFullscreen" />
            <div class="d-flex flex-grow-1 mainview">
                <MenuComp
                    show-only-on="BIG"
                    v-if="!isFullscreen"
                />
                <div
                    id="pagecontent"
                    class="flex-grow-1 overflow-auto h-100 d-flex flex-column mt-md-0 mt-5"
                >
                    <div class="flex-shrink-1 d-flex d-md-none flex-row border-bottom">
                        <!-- this is placed here so we get scroll on the entire view on mobile -->
                        <HeaderSmall v-if="!isFullscreen" />
                    </div>

                    <div
                        class="p-0 mb-3"
                        v-if="Event != null && Event.Deleted === true"
                    >
                        <AlertMessage :type="'danger'"><span v-html="$tm('event.eventdeleted', '', true)"></span></AlertMessage>
                    </div>
                    <MessageContainer
                        :show-all-messages="false"
                        class="mt-3 mt-md-0"
                    />

                    <PinnedViewsContainer />

                    <router-view v-slot="{ Component }">
                        <KeepAlive>
                            <component
                                v-if="useAccessValidator().CheckViewAccess()"
                                :is="Component"
                                :key="getUniqueViewKey()"
                            />
                            <NotAuthorizedView v-else />
                        </KeepAlive>
                    </router-view>
                </div>
            </div>
        </div>
        <ToastContainer></ToastContainer>
        <DialogSystemModalStack />
    </div>
</template>
<style scoped>
.mainview {
    /* flex hack so the height wont be bigger than the container. flex-grow will use the rest of the space but not limitied to the parent container. */
    height: 1px;
}
</style>
